import { useBrand } from 'hooks/useBrand'
import { ArrowLeft, ArrowRight, CalendarClock, Zap } from 'lucide-react'
import { useHistory } from 'react-router-dom'

type ConsultationChoiceStepProps = {
  onChooseScheduled: () => void
  onBack: () => void
}

/**
 * Passo de escolha do tipo de atendimento, exclusivo das marcas com Consulta
 * na Hora (hasInstantConsultation, ex. Vikura): após a identificação o
 * paciente escolhe entre a consulta agendada (segue para a triagem clínica do
 * agendamento) e a Consulta na Hora (vai para o funil imediato reaproveitando
 * a identificação recém feita).
 */
export default function ConsultationChoiceStep({ onChooseScheduled, onBack }: ConsultationChoiceStepProps) {
  const { config } = useBrand()
  const history = useHistory()
  const instantConsultationPath = `${config.pathPrefix ?? ''}/consulta-na-hora`

  const goToInstantConsultation = () => {
    // O paciente acabou de se identificar no agendamento — a Consulta na Hora
    // reaproveita a identificação em vez de pedir os dados de novo.
    history.push(instantConsultationPath, { fromSchedulingIdentification: true })
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex items-center gap-2 border-none bg-transparent p-0 text-sm font-semibold text-neutral-700">
        <ArrowLeft className="size-4" />
        Voltar
      </button>

      <div className="mb-8 flex flex-col items-center gap-3 text-center">
        <h1 className="font-spartan text-2xl font-bold text-gray-800 md:text-3xl">Como você quer ser atendido?</h1>
        <p className="max-w-md text-sm text-gray-500 md:text-base">
          Agende sua consulta para o melhor dia e horário ou fale com um especialista agora mesmo.
        </p>
      </div>

      {/* Cards brancos de propósito: o theme css das marcas pinta qualquer
          bg-primary-* com a cor cheia; o tom suave da marca vem da CSS var
          --app-green-soft, como nos demais passos do funil branded. */}
      <div className="flex flex-col items-stretch gap-4 md:flex-row">
        <button
          type="button"
          onClick={onChooseScheduled}
          className="flex flex-1 cursor-pointer flex-col items-center gap-3 rounded-2xl border border-solid border-gray-100 bg-white p-6 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
            <CalendarClock className="h-8 w-8 text-primary-700" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">Consulta agendada</h2>
          <p className="text-sm text-gray-500">Escolha o médico, o dia e o horário que ficam melhores para você.</p>
          <span className="mt-auto flex items-center gap-1 text-sm font-semibold text-primary-700">
            Agendar consulta
            <ArrowRight className="size-4" />
          </span>
        </button>

        <button
          type="button"
          onClick={goToInstantConsultation}
          className="flex flex-1 cursor-pointer flex-col items-center gap-3 rounded-2xl border border-solid border-gray-100 bg-white p-6 text-center shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
            <Zap className="h-8 w-8 text-primary-700" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">Consulta na hora</h2>
          <p className="text-sm text-gray-500">Fale com um especialista agora mesmo por videochamada, por R$ 99,00.</p>
          <span className="mt-auto flex items-center gap-1 text-sm font-semibold text-primary-700">
            Consultar agora
            <ArrowRight className="size-4" />
          </span>
        </button>
      </div>
    </div>
  )
}
